/**
 * Sticky product ATC: mirrors main PDP add-to-cart, syncs See in cart state,
 * and toggles body class when the primary ATC has scrolled above the viewport
 * (user scrolled past it). It does not show when the ATC is only below the fold.
 */
(function () {
  const BODY_CLASS_OFFSCREEN = 'product-main-atc-offscreen';
  const CHAT_BOTTOM_PX = '100px';
  const STYLE_TAG_ID = 'sticky-atc-chat-override-style';
  const NUDGE_MARK = 'data-sticky-atc-chat-nudge';

  let chatWidgetObserverStarted = false;
  /** @type {HTMLElement[]} */
  let chatTriggersTracked = [];
  /** @type {Map<HTMLElement, MutationObserver>} */
  const chatTriggerObservers = new Map();
  let chatPollId = null;

  function debounceLocal(fn, ms) {
    let t;
    return function () {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, arguments), ms);
    };
  }

  function isOffscreenMode() {
    return document.body.classList.contains(BODY_CLASS_OFFSCREEN);
  }

  const CHAT_SELECTORS = [
    '.chat-button.vf-chat-trigger',
    '.vf-chat-trigger.chat-button',
    'button.vf-chat-trigger',
    '.vf-chat-trigger',
    '[class*="vf-chat-trigger"]',
    '[class*="vf_chat"]',
    '[class*="VfChat"]',
  ];

  /**
   * App widgets may live in shadow roots; walk the tree and search each root.
   */
  function collectChatTriggersFromRoot(root, seen, out) {
    if (!root || !root.querySelectorAll) return;
    CHAT_SELECTORS.forEach((sel) => {
      try {
        root.querySelectorAll(sel).forEach((el) => {
          if (el instanceof HTMLElement && !seen.has(el)) {
            seen.add(el);
            out.push(el);
          }
        });
      } catch (e) {
        /* ignore invalid selectors */
      }
    });
    root.querySelectorAll('*').forEach((el) => {
      if (el.shadowRoot) collectChatTriggersFromRoot(el.shadowRoot, seen, out);
    });
  }

  function findChatTriggerElements() {
    const seen = new Set();
    const out = [];
    collectChatTriggersFromRoot(document.documentElement, seen, out);
    return out;
  }

  function getFixedOrStickyPositionTarget(start) {
    let el = start;
    for (let depth = 0; depth < 14 && el; depth++) {
      const cs = window.getComputedStyle(el);
      if (cs.position === 'fixed' || cs.position === 'sticky') {
        return el;
      }
      el = el.parentElement;
    }
    return start;
  }

  function applyChatBottomToTrigger(trigger) {
    if (!isOffscreenMode()) return;
    const target = getFixedOrStickyPositionTarget(trigger);
    target.style.setProperty('bottom', CHAT_BOTTOM_PX, 'important');
    target.setAttribute(NUDGE_MARK, '');
  }

  /** Re-apply without tearing down observers (safe to call often). */
  function touchChatPositions() {
    if (!isOffscreenMode()) return;
    findChatTriggerElements().forEach(applyChatBottomToTrigger);
    document.querySelectorAll(`[${NUDGE_MARK}]`).forEach((el) => {
      if (el instanceof HTMLElement) {
        el.style.setProperty('bottom', CHAT_BOTTOM_PX, 'important');
      }
    });
  }

  function setInjectedOverrideCss(active) {
    let st = document.getElementById(STYLE_TAG_ID);
    if (!st) {
      st = document.createElement('style');
      st.id = STYLE_TAG_ID;
      document.head.appendChild(st);
    }
    const b = 'body.' + BODY_CLASS_OFFSCREEN;
    if (active) {
      st.textContent =
        b +
        ' .chat-button.vf-chat-trigger,' +
        b +
        ' .vf-chat-trigger,' +
        b +
        ' button.vf-chat-trigger,' +
        b +
        ' [class*="vf-chat-trigger"]' +
        ' { bottom: ' +
        CHAT_BOTTOM_PX +
        ' !important; }';
    } else {
      st.textContent = '';
    }
  }

  function stopChatPoll() {
    if (chatPollId != null) {
      window.clearInterval(chatPollId);
      chatPollId = null;
    }
  }

  function startChatPoll() {
    stopChatPoll();
    chatPollId = window.setInterval(() => {
      if (!isOffscreenMode()) {
        stopChatPoll();
        return;
      }
      touchChatPositions();
    }, 450);
  }

  function teardownChatAdjustments() {
    chatTriggersTracked.forEach((trigger) => {
      const mo = chatTriggerObservers.get(trigger);
      if (mo) {
        mo.disconnect();
        chatTriggerObservers.delete(trigger);
      }
    });
    chatTriggersTracked = [];
    document.querySelectorAll(`[${NUDGE_MARK}]`).forEach((el) => {
      el.style.removeProperty('bottom');
      el.removeAttribute(NUDGE_MARK);
    });
  }

  function attachObserverToTrigger(trigger) {
    if (chatTriggerObservers.has(trigger)) return;
    const mo = new MutationObserver(() => touchChatPositions());
    mo.observe(trigger, { attributes: true, attributeFilter: ['style', 'class'] });
    chatTriggerObservers.set(trigger, mo);
    chatTriggersTracked.push(trigger);
  }

  /**
   * Full reset + attach. Only when sticky-offscreen mode toggles or on first need —
   * NOT on every scroll tick (that was breaking observers and inline styles).
   */
  function rebuildChatAdjustments() {
    const off = isOffscreenMode();
    if (!off) {
      stopChatPoll();
      teardownChatAdjustments();
      setInjectedOverrideCss(false);
      return;
    }

    teardownChatAdjustments();
    setInjectedOverrideCss(true);

    findChatTriggerElements().forEach((el) => {
      applyChatBottomToTrigger(el);
      attachObserverToTrigger(el);
    });
    startChatPoll();
    touchChatPositions();
  }

  function discoverNewChatTriggers() {
    if (!isOffscreenMode()) return;
    findChatTriggerElements().forEach((el) => {
      if (!chatTriggerObservers.has(el)) {
        applyChatBottomToTrigger(el);
        attachObserverToTrigger(el);
      }
    });
    touchChatPositions();
  }

  function ensureChatWidgetObserver() {
    if (chatWidgetObserverStarted) return;
    chatWidgetObserverStarted = true;
    const doDebounce = typeof debounce === 'function' ? debounce : debounceLocal;
    const debouncedDiscover = doDebounce(discoverNewChatTriggers, 160);
    new MutationObserver(debouncedDiscover).observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
    window.addEventListener('load', () => {
      if (isOffscreenMode()) rebuildChatAdjustments();
    });
    document.addEventListener('shopify:section:load', debouncedDiscover);
  }

  function initStickyAtc(wrapper) {
    const sectionId = wrapper.dataset.stickyAtcSection;
    if (!sectionId) return;

    const stickyBtn = wrapper.querySelector('[data-sticky-atc-button]');
    const mainBtn = document.getElementById(`ProductSubmitButton-${sectionId}`);
    const productFormEl = document.getElementById(`product-form-${sectionId}`);
    const productForm = productFormEl && productFormEl.closest('product-form');
    const form = productForm && productForm.querySelector('form');

    if (!stickyBtn || !mainBtn || !form) return;

    const doDebounce = typeof debounce === 'function' ? debounce : debounceLocal;

    function syncFromMain() {
      stickyBtn.classList.remove('see_in_cart--true', 'see_in_cart--false');
      mainBtn.classList.forEach((c) => {
        if (c.startsWith('see_in_cart--')) stickyBtn.classList.add(c);
      });
      if (![...stickyBtn.classList].some((c) => c.startsWith('see_in_cart--'))) {
        stickyBtn.classList.add('see_in_cart--false');
      }

      if (mainBtn.hasAttribute('disabled')) {
        stickyBtn.setAttribute('disabled', 'disabled');
      } else {
        stickyBtn.removeAttribute('disabled');
      }

      ['data-variant-id', 'data-product-id'].forEach((attr) => {
        if (mainBtn.hasAttribute(attr)) {
          stickyBtn.setAttribute(attr, mainBtn.getAttribute(attr));
        } else {
          stickyBtn.removeAttribute(attr);
        }
      });

      const ariaDisabled = mainBtn.getAttribute('aria-disabled');
      if (ariaDisabled != null) stickyBtn.setAttribute('aria-disabled', ariaDisabled);
      else stickyBtn.removeAttribute('aria-disabled');

      stickyBtn.classList.toggle('loading', mainBtn.classList.contains('loading'));

      const mainSpan = mainBtn.querySelector('span');
      const stickySpan = stickyBtn.querySelector('span');

      if (mainSpan && stickySpan) {
        const text = mainSpan.textContent.trim().toLowerCase();

        if (text === 'add to cart') {
          stickySpan.textContent = 'Add to Cart';
        } else {
          stickySpan.innerHTML = mainSpan.innerHTML;
        }
      }

      const mainSpinner = mainBtn.querySelector('.loading-overlay__spinner');
      const stickySpinner = stickyBtn.querySelector('.loading-overlay__spinner');
      if (mainSpinner && stickySpinner) {
        stickySpinner.classList.toggle('hidden', mainSpinner.classList.contains('hidden'));
      }
    }

    const mo = new MutationObserver(() => syncFromMain());
    mo.observe(mainBtn, {
      attributes: true,
      attributeFilter: ['disabled', 'class', 'aria-disabled', 'data-variant-id', 'data-product-id'],
      childList: true,
      subtree: true,
      characterData: true,
    });

    syncFromMain();

    stickyBtn.addEventListener('click', (e) => {
      if (stickyBtn.classList.contains('see_in_cart--true')) {
        return;
      }
      if (mainBtn.hasAttribute('disabled') || mainBtn.getAttribute('aria-disabled') === 'true') {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      form.requestSubmit();
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      const debouncedSync = doDebounce(syncFromMain, 60);
      subscribe(PUB_SUB_EVENTS.variantChange, debouncedSync);
      subscribe(PUB_SUB_EVENTS.cartUpdate, debouncedSync);
      subscribe(PUB_SUB_EVENTS.cartError, debouncedSync);
    }

    function updateMainAtcOffscreenBodyClass() {
      const vv = window.visualViewport;
      const vh = vv ? vv.height : window.innerHeight;
      const vw = vv ? vv.width : window.innerWidth;
      const offsetTop = vv ? vv.offsetTop : 0;
      const offsetLeft = vv ? vv.offsetLeft : 0;

      const r = mainBtn.getBoundingClientRect();
      // Sticky bar only after the shopper scrolls down far enough that the entire
      // main ATC sits above the visual viewport (passed the top). When the ATC is
      // only below the fold, r.bottom is still greater than offsetTop, so this stays false.
      const mainAtcScrolledPastTop = r.bottom <= offsetTop;

      const wasOff = document.body.classList.contains(BODY_CLASS_OFFSCREEN);
      document.body.classList.toggle(BODY_CLASS_OFFSCREEN, mainAtcScrolledPastTop);
      const nowOff = document.body.classList.contains(BODY_CLASS_OFFSCREEN);

      if (wasOff !== nowOff) {
        rebuildChatAdjustments();
      } else if (nowOff) {
        touchChatPositions();
      }
    }

    const debouncedOffscreen = doDebounce(updateMainAtcOffscreenBodyClass, 50);
    /** rAF-coalesced updates so the body class tracks scroll position during fast scroll, not only after pause. */
    let offscreenScrollRafId = null;
    function scheduleOffscreenFromScroll() {
      if (offscreenScrollRafId != null) return;
      offscreenScrollRafId = requestAnimationFrame(() => {
        offscreenScrollRafId = null;
        updateMainAtcOffscreenBodyClass();
      });
    }

    updateMainAtcOffscreenBodyClass();

    document.addEventListener('scroll', scheduleOffscreenFromScroll, true);
    window.addEventListener('resize', debouncedOffscreen);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', debouncedOffscreen);
      window.visualViewport.addEventListener('scroll', scheduleOffscreenFromScroll);
    }

    const sectionHost =
      document.getElementById(`shopify-section-${sectionId}`) ||
      wrapper.closest('.shopify-section') ||
      wrapper.closest('section');
    if (sectionHost && typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(debouncedOffscreen).observe(sectionHost);
    }
  }

  function boot() {
    const roots = document.querySelectorAll('[data-sticky-atc-root]');
    if (roots.length) {
      ensureChatWidgetObserver();
    }
    roots.forEach(initStickyAtc);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /** On PDP with sticky bar visible, run: __stickyAtcChatDebug() */
  window.__stickyAtcChatDebug = function () {
    const triggers = findChatTriggerElements();
    console.log('[sticky-atc] product-main-atc-offscreen=', document.body.classList.contains(BODY_CLASS_OFFSCREEN));
    console.log('[sticky-atc] matched elements:', triggers);
    triggers.forEach((t, i) => {
      const tgt = getFixedOrStickyPositionTarget(t);
      console.log('[sticky-atc]', i, 'trigger=', t, 'positionTarget=', tgt, 'computed=', window.getComputedStyle(tgt).position);
    });
  };
})();

